import { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
  Form,
} from '@predoc/cow';
import { CircleX } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ProviderFacilityFormFields } from './ProviderFacilityFormFields';
import { SuggestedProviders } from './SuggestedProviders';
import {
  providerFacilitySchema,
  type ProviderFacilityFormData,
} from './validation';
import { type ProviderSearchResult } from '../../../../types';
import { useProviderSearch } from '../../../../hooks/useProviderSearch';
import { defaultProviderFacilityFormValues } from '@/feature/patients/constants';

interface ProviderFacilityModalProps {
  title?: string;
  submitButtonText?: string;
  clearButtonText?: string;
  showAddAnotherButton?: boolean;
  initialValues?: Partial<ProviderFacilityFormData>;
  onSubmit: (provider: ProviderFacilityFormData) => void;
  onAddAnother?: (provider: ProviderFacilityFormData) => void;
  children?: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export const ProviderFacilityModal = ({
  title = 'Add a Provider/Facility',
  submitButtonText = 'Add',
  clearButtonText = 'Clear Form',
  showAddAnotherButton = true,
  initialValues,
  onSubmit,
  onAddAnother,
  children,
  open: controlledOpen,
  onOpenChange,
}: ProviderFacilityModalProps) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpen = onOpenChange || setInternalOpen;
  const [originalManualFormState, setOriginalManualFormState] =
    useState<ProviderFacilityFormData | null>(null);
  const [hasProviderBeenSelected, setHasProviderBeenSelected] = useState(false);
  const {
    searchResults,
    isSearching,
    hasMoreResults,
    hasSearchParams,
    loadMoreResults,
    handleFieldChange,
    lockSearchDuringFormPopulation,
    unlockSearchAfterFormPopulation,
    clearAllSearchFields,
  } = useProviderSearch();

  const formValues = useMemo(
    () => ({ ...defaultProviderFacilityFormValues, ...initialValues }),
    [initialValues],
  );

  const form = useForm<ProviderFacilityFormData>({
    resolver: zodResolver(providerFacilitySchema),
    defaultValues: formValues,
  });

  // Reset form with initial values when modal opens
  useEffect(() => {
    if (open) {
      form.reset(formValues);
    }
  }, [open, formValues, form]);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    form.reset(formValues);
    setOriginalManualFormState(null);
    setHasProviderBeenSelected(false);
    clearAllSearchFields();
  };

  const handleClearForm = () => {
    clearAllSearchFields();
    form.reset(defaultProviderFacilityFormValues);
    setOriginalManualFormState(null);
    setHasProviderBeenSelected(false);
  };

  const handleSubmit = form.handleSubmit((values) => {
    onSubmit(values);
    handleClose();
  });

  const handleAddAnotherSubmit = async () => {
    const isValid = await form.trigger();
    if (!isValid) return;
    onAddAnother?.(form.getValues());
    handleClearForm();
  };

  const handleSelectProvider = (provider: ProviderSearchResult) => {
    // Store manual typein on first provider selection
    if (!hasProviderBeenSelected) {
      const currentValues = form.getValues();
      setOriginalManualFormState(currentValues);
      setHasProviderBeenSelected(true);
    }

    lockSearchDuringFormPopulation();

    const currentValues = form.getValues();
    form.reset({
      ...currentValues,
      providerName: provider.providerName || '',
      facilityName: provider.facilityName || '',
      providerNpi: provider.providerNpi || undefined,
      facilityNpi: provider.facilityNpi || undefined,
      address1: provider.address?.address1 || '',
      address2: provider.address?.address2 || '',
      city: provider.address?.city || '',
      state: provider.address?.state || '',
      postalCode: provider.address?.postalCode || '',
      phoneNumber: provider.phoneNumbers?.[0] || '',
      faxNumber: provider.faxNumbers?.[0] || '',
      specialty: provider.specialties?.join(', ') || '',
    });

    unlockSearchAfterFormPopulation();
  };

  const handleUnselectProvider = () => {
    if (originalManualFormState) {
      lockSearchDuringFormPopulation();
      form.reset(originalManualFormState);
      setOriginalManualFormState(null);
      setHasProviderBeenSelected(false);
      unlockSearchAfterFormPopulation();
    }
  };

  return (
    <>
      {children && (
        <div onClick={handleOpen} style={{ display: 'inline-block' }}>
          {children}
        </div>
      )}
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent
          className="mx-auto flex !max-h-[90vh] !max-w-[1024px] flex-col"
          aria-describedby={undefined}
        >
          <DialogHeader className="border-b pb-4">
            <DialogTitle>{title}</DialogTitle>
          </DialogHeader>

          <Form {...form}>
            <div className="flex min-h-0 flex-1 overflow-y-auto">
              <div className="flex-[2]">
                <ProviderFacilityFormFields
                  lockSearchDuringFormPopulation={
                    lockSearchDuringFormPopulation
                  }
                  unlockSearchAfterFormPopulation={
                    unlockSearchAfterFormPopulation
                  }
                  onFieldChange={(field: string, value: string) => {
                    handleFieldChange(
                      field as
                        | 'providerName'
                        | 'facilityName'
                        | 'city'
                        | 'state'
                        | 'postalCode',
                      value,
                    );
                  }}
                />
              </div>

              <div className="flex-[1]">
                <SuggestedProviders
                  isLoading={isSearching}
                  searchResults={searchResults}
                  hasMoreResults={hasMoreResults}
                  hasSearchParams={hasSearchParams}
                  onLoadMore={loadMoreResults}
                  onSelectProvider={handleSelectProvider}
                  onUnselectProvider={handleUnselectProvider}
                />
              </div>
            </div>
          </Form>

          <DialogFooter className="border-t pt-4">
            <div className="flex w-full items-center justify-between">
              <Button
                type="button"
                variant="outline"
                onClick={handleClearForm}
                aria-label={clearButtonText}
              >
                {clearButtonText}
              </Button>
              <div className="flex items-center gap-2">
                {form.formState.isSubmitted && !form.formState.isValid && (
                  <div className="flex items-center gap-2 text-sm text-red-600">
                    <CircleX className="h-4 w-4" />
                    Please fix validation errors.
                  </div>
                )}
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleClose}
                    aria-label="Cancel"
                  >
                    Cancel
                  </Button>
                  {showAddAnotherButton && onAddAnother && (
                    <Button
                      type="button"
                      onClick={handleAddAnotherSubmit}
                      aria-label="Add & Add Another"
                    >
                      Add & Add Another
                    </Button>
                  )}
                  <Button
                    type="button"
                    onClick={handleSubmit}
                    aria-label={submitButtonText}
                  >
                    {submitButtonText}
                  </Button>
                </div>
              </div>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
